import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { tenantApi } from '@/api';
import { useUserGroupListQuery } from '@/hook';
import {
  AccountCircle,
  Close,
  VButton,
  VFlex,
  VInput,
  VModal,
  VStack,
  VTextButton,
  useUnleash,
} from '@/package';
import { useAuthStore, useCognitoStore } from '@/store';

import { PrivacyAgree } from '../Auth';
import { ChangeGroup, ChangeName, ChangeRole } from '../UserProfile';

const MyProfile = ({
  setVisible,
}: {
  setVisible: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const { user, isAdmin, isManager } = useAuthStore();
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isNewPasswordTouchced, setIsNewPasswordTouched] = useState(false);
  const [isConfirmTouchced, setIsConfirmTouched] = useState(false);
  const { data: groupList } = useUserGroupListQuery();
  const { setPromotionAgree } = useAuthStore();
  const { termsAgreements } = useCognitoStore();
  const { getVariant } = useUnleash();
  const enabledUserAgreementFlag =
    getVariant('vuex.cordoba.userAgreement')?.feature_enabled ?? false;
  const passwordRegex =
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d\W]{8,}$/;
  const validNewPassword = passwordRegex.test(newPassword);
  const validConfirmPassword = confirmPassword === newPassword;
  const onConfirmClick = () => {
    tenantApi
      .changePassword({ email: user.email, password: newPassword })
      .then(() => {
        setPasswordModalVisible(false);
        setNewPassword('');
        setConfirmPassword('');
      });
  };

  const updateMyUserInfo = () => {
    useAuthStore.getState().getMe();
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNewPassword('');
    setConfirmPassword('');
    setIsNewPasswordTouched(false);
    setIsConfirmTouched(false);
  }, [passwordModalVisible]);

  return (
    <>
      <VStack className="gap-[40px] px-8 py-6">
        <VFlex justify="between" className="w-full" items="center">
          <div className="text-headline-md">My account</div>
          <Close
            role="button"
            className="h-6 w-6 text-gray-400"
            onClick={() => setVisible(false)}
          />
        </VFlex>
        <VFlex gap={1.5} className="w-full">
          <AccountCircle className="h-[184px] w-[184px]" />

          <VStack className="w-74" gap={1}>
            <ChangeName selectedUser={user} />
            <VStack className="w-full" gap={0.25}>
              <VInput label="Email" value={user?.email ?? ''} readOnly fluid />
              <VFlex className="w-full" justify="end">
                <VTextButton
                  size="md"
                  onClick={() => setPasswordModalVisible(true)}
                >
                  {t('auth.passwordChangeButtonText.anagram')}
                </VTextButton>
              </VFlex>
            </VStack>
            <ChangeRole
              selectedUser={user}
              isUserEditable={isManager() || isAdmin()}
              updateMyUserInfo={updateMyUserInfo}
            />
            <ChangeGroup
              groupList={groupList ?? []}
              selectedUser={user}
              isUserEditable={isManager() || isAdmin()}
            />

            {enabledUserAgreementFlag && (
              <div className="mt-[24px]">
                <PrivacyAgree
                  hiddenItem={{ required: true }}
                  onChangeAgree={async agreementList => {
                    const nextMarketingPromotion = agreementList.find(
                      t => t.name === 'marketingPromotion',
                    )?.checked;
                    const nextConsentAITraining = agreementList.find(
                      t => t.name === 'consentAITraining',
                    )?.checked;

                    try {
                      await termsAgreements({
                        termsOfService: 'true',
                        personalInfoCollection: 'true',
                        personalInfoTransfer: 'true',
                        marketingPromotion: nextMarketingPromotion
                          ? 'true'
                          : 'false',
                        consentAITraining: nextConsentAITraining
                          ? 'true'
                          : 'false',
                      });

                      setPromotionAgree(nextMarketingPromotion ?? false);
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                  defaultCheckedMap={{
                    marketingPromotion: user.isMarketingPromotion,
                    consentAITraining: user.isConsentAITraining,
                  }}
                />
              </div>
            )}
          </VStack>
        </VFlex>
      </VStack>

      <VModal
        visible={passwordModalVisible}
        onUpdateVisible={setPasswordModalVisible}
        size="small"
      >
        <VStack className="w-full" gap={1.5}>
          <VFlex className="w-full" justify="between">
            <div className="text-body-xl">
              {t('auth.passwordChangeButtonText.anagram')}
            </div>
            <Close
              role="button"
              className="size-6 text-gray-400"
              onClick={() => setPasswordModalVisible(false)}
            />
          </VFlex>

          <VStack className="w-full" gap={1}>
            <VInput
              label={t('auth.passwordChangeNewPasswordPlaceholder.poppability')}
              type="password"
              fluid
              value={newPassword}
              valid={!isNewPasswordTouchced || validNewPassword}
              onChange={e => {
                setNewPassword(e.target.value);
                setIsNewPasswordTouched(true);
              }}
              comment={
                !isNewPasswordTouchced || validNewPassword
                  ? ''
                  : t('auth.passwordChangeNewPasswordErrorMessage.guitarist')
              }
              autoComplete="new-password"
              name="new-password"
            />
            <VInput
              type="password"
              fluid
              value={confirmPassword}
              onChange={e => {
                setConfirmPassword(e.target.value);
                setIsConfirmTouched(true);
              }}
              valid={
                !isConfirmTouchced ||
                (isConfirmTouchced && validConfirmPassword)
              }
              autoComplete="new-password"
              name="confirm-password"
            />
          </VStack>

          <VFlex justify="end" gap={0.25}>
            <VButton
              outlined
              size="sm"
              variant="secondary"
              onClick={() => setPasswordModalVisible(false)}
            >
              {t('modal.cancelButtonText.intimidate')}
            </VButton>
            <VButton
              size="sm"
              disabled={!isConfirmTouchced || !validConfirmPassword}
              onClick={() => onConfirmClick()}
            >
              {t('auth.passwordChangeButtonText.anagram')}
            </VButton>
          </VFlex>
        </VStack>
      </VModal>
    </>
  );
};

export default MyProfile;
